import api from '../../API/baseURL';
import standardAPI from '../../API/standardAPI';
import { handleAuthErrors } from '../../utils/vsCodeUtils';

// API endpoints for VSCode Dashboard
const DASHBOARD_ENDPOINTS = {
  GET_USER_INFO: '/payment/get_user_info',
  GET_USER_SUBSCRIPTIONS: '/payment/get_unified_subscription_info',
  GET_USER_TOKEN_USAGE: '/get_user_token_usage', // Deprecated - use new analytics endpoints
  GET_TOKEN_USAGE_BY_PERIOD: '/analytics/token-usage', // New: GET /analytics/token-usage/{period}
  GET_TOKEN_USAGE_HISTORY: '/analytics/token-usage-history', // New: GET /analytics/token-usage-history
  GET_USER_PAYMENT_INFO: '/payment/get_user_payment_info',
  SEND_CANCELLATION_EMAIL: '/vs-code/auth/send_sub_cancellation_as_email',
  UPDATE_USER_INFO: '/vs-code/auth/update_user_info',
  GET_BILLING_HISTORY: '/payment/get_user_billing_history',
  GET_INVOICES: '/payment/get_user_invoices'
};

// Get user information
export const getUserInfo = async (userId) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(`${DASHBOARD_ENDPOINTS.GET_USER_INFO}?id=${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('User Info:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user info:', error);
    handleAuthErrors(error);
  }
};

// Get user subscriptions and plans
export const getUserSubscriptions = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(DASHBOARD_ENDPOINTS.GET_USER_SUBSCRIPTIONS, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('Unified Subscription Info:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    handleAuthErrors(error);
  }
};

// Get user token usage (Deprecated - use getTokenUsageByPeriod or getTokenUsageHistory)
export const getUserTokenUsage = async (params) => {
  try {
    const queryParams = new URLSearchParams(params).toString();

    const response = await standardAPI.get(`${DASHBOARD_ENDPOINTS.GET_USER_TOKEN_USAGE}?${queryParams}`);
    console.log('Token Usage:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching token usage:', error);
    handleAuthErrors(error);
  }
};

// Get token usage by period (New endpoint for individuals/members)
// Returns: { data: number, status: 200 }
export const getTokenUsageByPeriod = async (period) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(`${DASHBOARD_ENDPOINTS.GET_TOKEN_USAGE_BY_PERIOD}/${period}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('Token Usage by Period:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching token usage by period:', error);
    handleAuthErrors(error);
  }
};

// Get token usage history (New endpoint for individuals/members)
// Returns: { data: { total, "1": { total, "1": { total } } }, status: 200 }
export const getTokenUsageHistory = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(DASHBOARD_ENDPOINTS.GET_TOKEN_USAGE_HISTORY, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('Token Usage History:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching token usage history:', error);
    handleAuthErrors(error);
  }
};

// Get user payment information
export const getUserPaymentInfo = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(DASHBOARD_ENDPOINTS.GET_USER_PAYMENT_INFO, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('User Payment Info:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user payment info:', error);
    handleAuthErrors(error);
  }
};

// Cancel a Stripe subscription plan (real cancellation via the backend).
export const cancelStripeSubscription = async (plan) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.delete(
      `/payment/cancel_subscription_plan?plan=${encodeURIComponent(plan || '')}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error cancelling stripe subscription:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Send subscription cancellation email
export const sendCancellationEmail = async (cancellationData) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post(DASHBOARD_ENDPOINTS.SEND_CANCELLATION_EMAIL, {
      reason: cancellationData.reason,
      additional_feedback: cancellationData.feedback
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Cancellation email sent:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending cancellation email:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Update user information
export const updateUserInfo = async (userInfo) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.patch(DASHBOARD_ENDPOINTS.UPDATE_USER_INFO, {
      fullname: userInfo.fullname,
      company_name: userInfo.company_name
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('User info updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating user info:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get billing history
export const getBillingHistory = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(DASHBOARD_ENDPOINTS.GET_BILLING_HISTORY, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('Billing History:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching billing history:', error);
    handleAuthErrors(error);
  }
};

// Get invoices
export const getInvoices = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(DASHBOARD_ENDPOINTS.GET_INVOICES, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    console.log('Invoices:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    handleAuthErrors(error);
  }
};

// Upgrade plan.
// For recurring tiers the backend returns success / fallback_update_payment_method_link /
// requires_payment_update as before. For the one-off Lifetime price the backend returns
// { checkout_url, is_lifetime: true, requires_checkout: true } and the caller must redirect
// to checkout_url. success_url / cancel_url are optional and only consumed for the
// hosted-checkout (lifetime) path; they default to the lifetime success/cancel routes.
export const upgradePlan = async (newPriceId, successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const body = {
      new_price_id: newPriceId,
      success_url: successUrl || `${origin}/vscode/dashboard?lifetime=success`,
      cancel_url: cancelUrl || `${origin}/pricing`
    };
    const response = await api.post('/payment/upgrade_plan', body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Upgrade plan:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error upgrading plan:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Lifetime plan — email a one-time purchase invoice to the user after they
// agree to the purchase agreement in LifetimeAgreementModal.
export const sendLifetimeInvoice = async (email) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post('/payment/send_lifetime_invoice', { email }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Send lifetime invoice:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending lifetime invoice:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Update subscription payment method — create Stripe setup checkout session (no charge)
export const updateSubscriptionPaymentMethod = async (successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post('/payment/update_payment_method', {
      success_url: successUrl,
      cancel_url: cancelUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Update payment method:', response.data);
    return response.data; // { checkout_url }
  } catch (error) {
    console.error('Error updating subscription payment method:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Ultra credit recharge — create Stripe checkout session
export const rechargeCredits = async (amount, successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post('/payg/recharge_credits', {
      amount,
      success_url: successUrl,
      cancel_url: cancelUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data; // { checkout_url, session_id, amount, credit_value }
  } catch (error) {
    console.error('Error creating credit recharge checkout:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Ultra credit balance
export const fetchCreditBalance = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get('/payg/fetch_credit_balance', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data; // { credit_balance }
  } catch (error) {
    console.error('Error fetching credit balance:', error);
    handleAuthErrors(error);
    return { credit_balance: 0 };
  }
};

export default DASHBOARD_ENDPOINTS;
